import { useState, useEffect } from 'react';
import { Plus, Megaphone, Trash2, Bell, Gift, AlertTriangle, Info, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

const typeConfig = {
  info: { icon: Info, label: 'Info', color: 'bg-blue-100 text-blue-700' },
  warning: { icon: AlertTriangle, label: 'Warning', color: 'bg-amber-100 text-amber-700' },
  promo: { icon: Gift, label: 'Promotion', color: 'bg-green-100 text-green-700' },
  urgent: { icon: Bell, label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

export default function AdminAnnouncements() {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    announcement_type: 'info',
    target_audience: 'all',
    link_url: '',
    link_text: '',
    show_until: '',
    dismissible: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [token]);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setAnnouncements(await response.json());
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Announcement created');
        setIsDialogOpen(false);
        setFormData({
          title: '',
          message: '',
          announcement_type: 'info',
          target_audience: 'all',
          link_url: '',
          link_text: '',
          show_until: '',
          dismissible: true
        });
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create announcement');
      }
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const toggleActive = async (announcement) => {
    // For now, just delete inactive ones
    if (announcement.is_active) {
      deleteAnnouncement(announcement.announcement_id);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" />
            Announcements
          </h1>
          <p className="text-slate-500 mt-1">Broadcast messages to customers and staff</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-600" data-testid="add-announcement-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Create Announcement</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Announcement title"
                  required
                />
              </div>

              <div>
                <Label>Message *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Your message..."
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={formData.announcement_type} onValueChange={(v) => setFormData(prev => ({ ...prev, announcement_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(typeConfig).map(([value, config]) => {
                        const Icon = config.icon;
                        return (
                          <SelectItem key={value} value={value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {config.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Audience</Label>
                  <Select value={formData.target_audience} onValueChange={(v) => setFormData(prev => ({ ...prev, target_audience: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="customers">Customers Only</SelectItem>
                      <SelectItem value="staff">Staff Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Link URL (optional)</Label>
                  <Input
                    value={formData.link_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Link Text</Label>
                  <Input
                    value={formData.link_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_text: e.target.value }))}
                    placeholder="Learn more"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Show Until</Label>
                  <Input
                    type="datetime-local"
                    value={formData.show_until}
                    onChange={(e) => setFormData(prev => ({ ...prev, show_until: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.dismissible}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, dismissible: v }))}
                  />
                  <Label>Allow dismiss</Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-600">
                  Publish
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-24" />
            </Card>
          ))}
        </div>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No announcements yet</p>
            <p className="text-sm text-slate-400">Create your first announcement to broadcast to users</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => {
            const config = typeConfig[announcement.announcement_type] || typeConfig.info;
            const Icon = config.icon;
            
            return (
              <Card key={announcement.announcement_id} className={!announcement.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{announcement.title}</h3>
                          <Badge className={config.color}>{config.label}</Badge>
                          <Badge variant="outline" className="text-xs">
                            {announcement.target_audience}
                          </Badge>
                        </div>
                        <p className="text-slate-600 text-sm">{announcement.message}</p>
                        {announcement.link_url && (
                          <a href={announcement.link_url} className="text-sm text-primary hover:underline mt-1 inline-block">
                            {announcement.link_text || announcement.link_url}
                          </a>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>Created: {new Date(announcement.created_at).toLocaleString()}</span>
                          {announcement.show_until && (
                            <span>Expires: {new Date(announcement.show_until).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(announcement.announcement_id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
